import { useState, useMemo, useEffect, Fragment } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import * as XLSX from 'xlsx'
import { useAppStore } from '@/store'
import { useAllowedEmpresas } from '@/hooks/useAllowedEmpresas'
import { useIsEmpresaAll }    from '@/hooks/useIsEmpresaAll'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { useFiltrosPersistentes } from '@/hooks/useFiltrosPersistentes'
import { predicadoArticulos, type CoincideArticulo } from '@/utils/filtroArticulo'
import { filaResaltada } from '@/components/pedidos-ventas/formato'
import { MultiFiltro } from '@/components/common/MultiFiltro'
import { BarraFiltrosActivos, filtrosDe, type FiltroActivo } from '@/components/common/BarraFiltrosActivos'
import { pedidosCompraApi } from '@/services/api'
import { AdjuntosModal } from '@/components/pedidosCompra/AdjuntosModal'

import type { AggColumn, MarcasFilas, PedidoCompraRow, PedidoCompraLinea, PedidoCompraSortKey } from '@/types'
import { useColumnAggregations } from '@/hooks/useColumnAggregations'
import { AggregationMenu }   from '@/components/common/AggregationMenu'
import { TableTotalsFooter } from '@/components/common/TableTotalsFooter'
import { KpiCard }    from '@/components/planning/KpiCard'
import { PAGE_KPIS }  from '@/config/uiFlags'
import { PageHeader } from '@/components/common/PageHeader'
import { Loader } from '@/components/common/Loader'
import { EmptyState } from '@/components/common/EmptyState'
import { Pagination } from '@/components/common/Pagination'
import { StatusBadge } from '@/components/common/StatusBadge'
import { filterFieldCls, filterLabelCls, filterCardCls } from '@/components/common/FilterBar'
import { RefreshButton }   from '@/components/common/RefreshButton'
import { DateRangePicker } from '@/components/common/DateRangePicker'
import { SortableHeader, SortDirButton } from '@/components/common/SortableHeader'
import { TableToolbar }   from '@/components/common/TableToolbar'
import { SubTableWrapper } from '@/components/common/SubTableWrapper'
import { SubTotalsRow } from '@/components/common/SubTotalsRow'
import { BarraMarcas, CabeceraMarca, FilaMarcable, MarcaCelda, useMarcasFilas } from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'
import { columnasPieCompra } from '@/utils/subtotalesLineas'
import { ColumnsMenu }     from '@/components/common/ColumnsMenu'
import type { ColumnItem } from '@/components/common/ColumnsMenu'
import { desdeInicial } from '@/utils/rangoPorDefecto'
import { useVariasEmpresas } from '@/hooks/useVariasEmpresas'

function fmtDate(iso?: string) {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}
function fmtN(n: number) {
  if (!n) return '—'
  return n.toLocaleString('es-ES', { maximumFractionDigits: 0 })
}
/** Como fmtN pero el cero se escribe «0».
 *  En un total, «cero unidades pendientes» es un dato; la raya se leería como
 *  «sin dato». La fila de totales ya distingue el caso sin valores que sumar. */
function fmtQty(n: number) {
  if (!n) return '0'
  return fmtN(n)
}
function fmtEur(n: number) {
  if (!n) return '—'
  return n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
}


// ── Columnas de la tabla de pedidos ───────────────────────────────────────────
// Fuente única para la cabecera, el menú de columnas y el pie de totales. El
// cuerpo se sigue pintando a mano: cada celda tiene su propio formato.

interface PcCol {
  key: string
  labelKey?: string
  sortKey?: PedidoCompraSortKey
  align?: 'right'
  /** Columna de referencia conmutable desde el menú de columnas. */
  refCol?: boolean
  onlyEmpresaAll?: boolean
}

const COLS: PcCol[] = [
  { key: 'marca' },
  { key: 'expand' },
  { key: 'attachments',     labelKey: 'purchaseOrders.col.attachments' },
  { key: 'purchid',         labelKey: 'purchaseOrders.col.orderId',         sortKey: 'purchid' },
  { key: 'customerOrderNo', labelKey: 'purchaseOrders.col.customerOrderNo', sortKey: 'numeroPedidoCliente',    refCol: true },
  { key: 'containerNo',     labelKey: 'purchaseOrders.col.containerNo',     sortKey: 'numeroContenedor',       refCol: true },
  { key: 'shipperInvoice',  labelKey: 'purchaseOrders.col.shipperInvoice',  sortKey: 'numeroFacturaExpedidor', refCol: true },
  { key: 'bl',              labelKey: 'purchaseOrders.col.bl',              sortKey: 'bl',                     refCol: true },
  { key: 'dvd',             labelKey: 'purchaseOrders.col.dvd',             sortKey: 'dvd',                    refCol: true },
  { key: 'dataareaid',      labelKey: 'purchaseOrders.col.company',         onlyEmpresaAll: true },
  { key: 'nombreProveedor', labelKey: 'purchaseOrders.col.supplier',        sortKey: 'nombreProveedor' },
  { key: 'purchstatus',     labelKey: 'purchaseOrders.col.status',          sortKey: 'purchstatus' },
  { key: 'fechaEntrega',    labelKey: 'purchaseOrders.col.orderDate',       sortKey: 'fechaEntrega', align: 'right' },
  { key: 'qtyPendiente',    labelKey: 'purchaseOrders.col.qtyPending',      sortKey: 'qtyPendiente', align: 'right' },
  { key: 'importeTotal',    labelKey: 'purchaseOrders.col.amount',          sortKey: 'importeTotal', align: 'right' },
]

const REF_COL_KEYS = COLS.filter(c => c.refCol).map(c => c.key)

// El importe se desglosa por divisa: con Empresa.All pueden convivir pedidos en
// monedas distintas y una suma única no significaría nada.
const AGG_COLS: AggColumn<PedidoCompraRow>[] = [
  { key: 'qtyPendiente', numeric: true, decimals: 2, defaultAgg: 'sum' },
  { key: 'importeTotal', numeric: true, currencyKey: 'moneda', decimals: 2, defaultAgg: 'sum' },
]

const AGG_KEYS = new Set(AGG_COLS.map(c => c.key))

const PAGE_SIZE = 50
/** Pedidos que se abren solos al buscar. Mismo criterio que Pedidos de venta:
 *  cada fila desplegada dispara su consulta de líneas. */
const MAX_AUTO_DESPLIEGUE = 10

const TOTAL_COLS = 17  // colSpan de la fila de líneas (holgado: cubre todas las columnas de referencia + Empresa + el canalón de marcas)
const LINE_COLS  = 13  // nº columnas de la subtabla de líneas, canalón de marcas incluido (colSpan de las bandas de factura)
const SIN_FACTURAR = '__SIN_FACTURAR__'

function LineasRow({ purchid, dark, coincide, onVerAdjuntosFactura, marcas, clavePedido }: {
  purchid: string
  dark: boolean
  marcas: MarcasFilas
  /** Clave de la fila padre. La de cada línea se construye a partir de ella,
   *  así que la línea 3 de un pedido y la 3 de otro nunca comparten marca. */
  clavePedido: string
  /** Casa la línea con la búsqueda: resalta las que la provocaron. Si la
   *  coincidencia venía sólo de la cabecera —un BL, un contenedor— aquí no
   *  casará ninguna, y eso es correcto: no se inventa una línea. */
  coincide?: CoincideArticulo | null
  onVerAdjuntosFactura: (recid: string, factura: string) => void
}) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['pedido-lineas', purchid],
    queryFn:  () => pedidosCompraApi.getLineas(purchid),
    staleTime: 5 * 60 * 1000,
  })
  const lineas: PedidoCompraLinea[] = (data?.data ?? []) as PedidoCompraLinea[]

  // Agrupar líneas por NÚMERO de factura / "Sin facturar" (recid_factura es por
  // línea, solo se usa para el botón de adjuntos). Hooks antes de early-returns.
  const grupos = useMemo(() => {
    const map = new Map<string, {
      key: string
      esSinFacturar: boolean
      factura?: string
      recid?: string
      fecha?: string
      moneda?: string
      lineas: PedidoCompraLinea[]
    }>()
    for (const l of lineas) {
      const facturaNum = (l.factura ?? '').trim()
      const recid      = (l.recidFactura ?? '').trim()
      const esSinFacturar = !facturaNum
      const key = esSinFacturar ? SIN_FACTURAR : facturaNum
      let g = map.get(key)
      if (!g) {
        g = {
          key, esSinFacturar,
          factura: facturaNum || undefined,
          recid:   recid || undefined,
          fecha:   l.primeraFechaFactura,
          moneda:  l.moneda,
          lineas:  [],
        }
        map.set(key, g)
      }
      g.lineas.push(l)
    }
    return [...map.values()].sort((a, b) => {
      if (a.esSinFacturar) return 1
      if (b.esSinFacturar) return -1
      return (a.fecha ?? '').localeCompare(b.fecha ?? '')
    })
  }, [lineas])

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  function toggleGrupo(k: string) {
    setCollapsed(prev => {
      const next = new Set(prev)
      next.has(k) ? next.delete(k) : next.add(k)
      return next
    })
  }

  const subTh    =`px-2 py-1.5 text-left text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${dark ? 'text-slate-500' : 'text-slate-400'}`
  const subTd    = `px-2 py-1.5 text-[11px] whitespace-nowrap ${dark ? 'text-slate-300' : 'text-slate-700'}`
  const mutedTxt = dark ? 'text-slate-500' : 'text-slate-400'
  const divider  = dark ? 'border-white/4' : 'border-slate-100'

  const ESTADO_COLOR: Record<string, string> = {
    'Pedido abierto': 'bg-sky-500/15 text-sky-400',
    'Recibido':       'bg-emerald-500/15 text-emerald-500',
    'Facturado':      'bg-purple-500/15 text-purple-400',
    'Cancelado':      'bg-red-500/15 text-red-500',
  }

  if (isLoading) return (
    <SubTableWrapper dark={dark} colSpan={TOTAL_COLS}>
      <Loader label={t('purchaseOrders.line.loading')} size={10} className="py-3" />
    </SubTableWrapper>
  )

  if (isError) return (
    <SubTableWrapper dark={dark} colSpan={TOTAL_COLS}>
      <div className="pl-10 py-3">
        <span className={`text-[11px] ${dark ? 'text-red-400' : 'text-red-600'}`}>
          {t('purchaseOrders.line.error')}
        </span>
      </div>
    </SubTableWrapper>
  )

  if (lineas.length === 0) return (
    <SubTableWrapper dark={dark} colSpan={TOTAL_COLS}>
      <div className="pl-10 py-3">
        <span className={`text-[11px] ${mutedTxt}`}>{t('purchaseOrders.line.none')}</span>
      </div>
    </SubTableWrapper>
  )

  const renderLinea = (l: PedidoCompraLinea, key: string, clave: string) => (
    <FilaMarcable
      key={key}
      marcas={marcas}
      clave={clave}
      dark={dark}
      className={`border-t ${divider} ${
        coincide?.(l.articulo, l.descripcion) ? filaResaltada(dark) : ''
      }`}
    >
      <MarcaCelda marcas={marcas} clave={clave} dark={dark} densa />
      <td className={`${subTd} tabular-nums ${mutedTxt}`}>{l.numLinea ?? '—'}</td>
      <td className={subTd}>
        <div className="max-w-[180px]">
          <div className="font-semibold truncate text-[11px]" title={fmtArt(l.articulo)}>{fmtArt(l.articulo) || '—'}</div>
          <div className={`text-[9px] truncate ${mutedTxt}`} title={l.descripcion}>{l.descripcion}</div>
        </div>
      </td>
      <td className={subTd}>
        <div className="text-[10px]">{l.familia ?? '—'}</div>
        {l.subfamilia && <div className={`text-[9px] ${mutedTxt}`}>{l.subfamilia}</div>}
      </td>
      <td className={`${subTd} text-right tabular-nums`}>{fmtN(l.qtyPedida)}</td>
      <td className={`${subTd} text-right tabular-nums ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>
        {fmtN(l.qtyRecibida)}
      </td>
      <td className={`${subTd} text-right tabular-nums font-semibold`}>{fmtN(l.qtyPendiente)}</td>
      <td className={`${subTd} ${mutedTxt}`}>{l.um ?? '—'}</td>
      <td className={`${subTd} text-right tabular-nums`}>
        {l.precioUnitario ? l.precioUnitario.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : '—'}
      </td>
      <td className={`${subTd} text-right tabular-nums font-semibold`}>
        {l.importePendiente ? fmtEur(l.importePendiente) : '—'}
        {l.moneda && l.moneda !== 'EUR' && (
          <div className={`text-[9px] font-normal ${mutedTxt}`}>{l.moneda}</div>
        )}
      </td>
      <td className={subTd}>
        {l.estadoLinea ? (
          <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${
            ESTADO_COLOR[l.estadoLinea] ?? (dark ? 'bg-white/8 text-slate-300' : 'bg-slate-100 text-slate-500')
          }`}>
            {t('purchaseOrders.status.' + l.estadoLinea, { defaultValue: l.estadoLinea })}
          </span>
        ) : '—'}
      </td>
      <td className={`${subTd} text-right tabular-nums`}>{fmtDate(l.fechaEntrega)}</td>
      <td className={`${subTd} text-right tabular-nums`}>{fmtDate(l.fechaConfirmada)}</td>
    </FilaMarcable>
  )

  return (
    <SubTableWrapper dark={dark} colSpan={TOTAL_COLS}>
      <div className="pl-8 pr-4 py-2 overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className={`border-b ${divider}`}>
              <CabeceraMarca />
              <th className={subTh}>{t('purchaseOrders.line.lineNum')}</th>
              <th className={subTh}>{t('purchaseOrders.line.article')}</th>
              <th className={subTh}>{t('purchaseOrders.line.family')}</th>
              <th className={`${subTh} text-right`}>{t('purchaseOrders.line.qtyOrdered')}</th>
              <th className={`${subTh} text-right`}>{t('purchaseOrders.line.qtyReceived')}</th>
              <th className={`${subTh} text-right`}>{t('purchaseOrders.line.qtyPending')}</th>
              <th className={subTh}>{t('purchaseOrders.line.um')}</th>
              <th className={`${subTh} text-right`}>{t('purchaseOrders.line.unitPrice')}</th>
              <th className={`${subTh} text-right`}>{t('purchaseOrders.line.pendingAmount')}</th>
              <th className={subTh}>{t('purchaseOrders.line.status')}</th>
              <th className={`${subTh} text-right`}>{t('purchaseOrders.line.deliveryDate')}</th>
              <th className={`${subTh} text-right`}>{t('purchaseOrders.line.confirmedDate')}</th>
            </tr>
          </thead>
          <tbody>
            {grupos.map(g => {
              const isCol   = collapsed.has(g.key)
              const qtyFact = g.lineas.reduce((s, l) => s + (l.qtyFacturada ?? 0), 0)
              const impFact = g.lineas.reduce((s, l) => s + (l.importeFacturado ?? 0), 0)
              const qtyPend = g.lineas.reduce((s, l) => s + (l.qtyPendiente ?? 0), 0)
              const impPend = g.lineas.reduce((s, l) => s + (l.importePendiente ?? 0), 0)
              const moneda  = g.moneda && g.moneda !== 'EUR' ? ` ${g.moneda}` : ''

              const bandBg = g.esSinFacturar
                ? (dark ? 'bg-amber-500/[0.07]' : 'bg-amber-50/70')
                : (dark ? 'bg-sky-500/[0.07]'  : 'bg-sky-50/70')
              const sumTxt = dark ? 'text-slate-200' : 'text-slate-700'

              return (
                <Fragment key={g.key}>
                  {/* Banda de factura / sin facturar */}
                  <tr className={`border-t ${divider} ${bandBg}`}>
                    <td colSpan={LINE_COLS} className="px-2 py-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => toggleGrupo(g.key)}
                          className="flex items-center gap-2 cursor-pointer group"
                        >
                          <svg className={`w-3 h-3 shrink-0 transition-transform duration-150 ${isCol ? '' : 'rotate-90'} ${dark ? 'text-slate-400' : 'text-slate-500'}`}
                            viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 11l4-4-4-4"/>
                          </svg>
                          {g.esSinFacturar ? (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${
                              dark ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {t('purchaseOrders.invoice.unbilled')}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5">
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${
                                dark ? 'bg-sky-500/15 text-sky-300' : 'bg-sky-100 text-sky-700'
                              }`}>
                                {t('purchaseOrders.invoice.invoice')}
                              </span>
                              <span className={`text-[12px] font-mono font-semibold ${dark ? 'text-slate-100' : 'text-slate-800'}`}>
                                {g.factura}
                              </span>
                              {g.fecha && (
                                <span className={`text-[10px] tabular-nums ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                                  · {fmtDate(g.fecha)}
                                </span>
                              )}
                            </span>
                          )}
                          <span className={`text-[10px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                            · {t('purchaseOrders.invoice.lines', { count: g.lineas.length })}
                          </span>
                        </button>

                        <div className="flex items-center gap-3 ml-auto text-[10px]">
                          {g.esSinFacturar ? (
                            <span className={sumTxt}>
                              {t('purchaseOrders.invoice.pendingQty')}:{' '}
                              <span className="tabular-nums font-semibold">{fmtN(qtyPend)}</span>
                              {impPend ? <span className="tabular-nums font-semibold ml-2">{fmtEur(impPend)}</span> : null}
                            </span>
                          ) : (
                            <span className={sumTxt}>
                              {t('purchaseOrders.invoice.billedQty')}:{' '}
                              <span className="tabular-nums font-semibold">{fmtEur(impFact)}{impFact ? moneda : ''}</span>
                              {qtyFact ? (
                                <span className={`tabular-nums ml-2 ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                                  · {fmtN(qtyFact)} {t('purchaseOrders.invoice.units')}
                                </span>
                              ) : null}
                            </span>
                          )}
                          {g.recid && (
                            <button
                              onClick={() => onVerAdjuntosFactura(g.recid!, g.factura ?? g.recid!)}
                              title={t('purchaseOrders.invoice.viewDocs')}
                              className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg border transition-colors cursor-pointer font-medium ${
                                dark
                                  ? 'border-white/10 bg-white/5 text-slate-300 hover:bg-sky-500/15 hover:text-sky-300 hover:border-sky-500/30'
                                  : 'border-slate-200 bg-white text-slate-600 hover:bg-sky-50 hover:text-sky-700 hover:border-sky-200 shadow-sm'
                              }`}
                            >
                              <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 2H4a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V6L9 2z"/>
                                <path d="M9 2v4h4M6 9h4M6 11.5h2.5"/>
                              </svg>
                              <span>{t('purchaseOrders.invoice.docs')}</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                  {!isCol && g.lineas.map((l, i) => renderLinea(
                    l,
                    `${g.key}-${l.numLinea ?? ''}-${i}`,
                    claveFila(clavePedido, g.key, l.numLinea),
                  ))}
                  {/* El total de la banda va aquí y no en un `tfoot` porque todas
                      las bandas comparten la misma tabla: cada una remata con su
                      fila, justo debajo de sus líneas. */}
                  {!isCol && (
                    <SubTotalsRow
                      dark={dark}
                      rows={g.lineas}
                      // El canalón de marcas es una columna más de la rejilla:
                      // sin su hueco, el pie se desplazaría una celda.
                      columnas={[
                        { key: '__marca__' },
                        ...columnasPieCompra({ fmtCantidad: fmtQty, fmtImporte: fmtEur }),
                      ]}
                    />
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </SubTableWrapper>
  )
}

/** Fecha ISO a dd/mm/aaaa para el chip. Un guion cuando el extremo está abierto. */
function fechaCorta(iso: string): string {
  return iso ? iso.split('-').reverse().join('/') : '…'
}

export default function PedidosCompraPage() {
  const { dark } = useAppStore()
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const allowedEmpresas = useAllowedEmpresas()
  const isEmpresaAll    = useIsEmpresaAll()
  // Marcas del usuario: pedidos y líneas comparten ámbito, porque la clave de
  // una línea empieza por la de su pedido y no pueden colisionar.
  const marcas = useMarcasFilas('pedidosCompra')
  const [searchParams, setSearchParams] = useSearchParams()

  // Los filtros aguantan irse a otra pantalla y volver, durante la sesión. Se
  // leen AQUÍ, en los inicializadores: al restaurar, la URL todavía está pelada
  // en este render, y el rango de fechas es parámetro de la consulta al backend.
  // Las fechas SÍ se recuerdan: esta pantalla no calcula ningún rango por
  // defecto (vacío = sin filtro), así que reponer el que el usuario escribió no
  // puede vaciarle la tabla contra un default que se mueve solo.
  const { paramsIniciales: params0, olvidar } = useFiltrosPersistentes('pedidosCompra')

  const [desde, setDesde] = useState(desdeInicial(params0.get('desde')))
  const [hasta, setHasta] = useState(params0.get('hasta') ?? '')

  // Términos de búsqueda combinados con OR, en la URL separados por «|»: ni un
  // nº de pedido, ni un contenedor, ni un BL lo contienen.
  const [terminos, setTerminos] = useState<string[]>(
    () => (params0.get('q') ?? '').split('|').filter(Boolean),
  )
  const [empresa, setEmpresa] = useState(params0.get('emp')    ?? '')
  const [estado,  setEstado]  = useState(params0.get('estado') ?? '')
  const [difRecepcion, setDifRecepcion] = useState(params0.get('difrec') === '1')
  const [sortKey, setSortKey] = useState<PedidoCompraSortKey>(
    (params0.get('sort') as PedidoCompraSortKey) ?? 'fechaEntrega',
  )
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    (params0.get('dir') as 'asc' | 'desc') ?? 'asc',
  )
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Todo el estado de consulta sube a la URL, igual que en Pedidos de venta.
  // Antes sólo subían las fechas, aunque el resto YA se leía al montar: el bucle
  // estaba a medias, así que ni un enlace a la lista filtrada ni el recuerdo de
  // la sesión podían incluir buscador, empresa, estado ni orden.
  useEffect(() => {
    const p = new URLSearchParams()
    const poner = (clave: string, valor: string) => { if (valor) p.set(clave, valor) }
    poner('desde', desde)
    poner('hasta', hasta)
    poner('q', terminos.join('|'))
    poner('emp', empresa)
    poner('estado', estado)
    if (difRecepcion) p.set('difrec', '1')
    if (sortKey !== 'fechaEntrega') p.set('sort', sortKey)
    if (sortDir !== 'asc') p.set('dir', sortDir)
    // Sólo se escribe si cambia algo: si no, cada render dispararía una
    // navegación y el historial del navegador quedaría inservible.
    if (p.toString() !== searchParams.toString()) {
      setSearchParams(p, { replace: true })
    }
  }, [desde, hasta, terminos, empresa, estado, difRecepcion, sortKey, sortDir, searchParams, setSearchParams])

  function toggleExpand(key: string) {
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const [adjuntosPurchid, setAdjuntosPurchid] = useState<string | null>(null)
  const [facturaAdj, setFacturaAdj] = useState<{ recid: string; factura: string } | null>(null)
  const [exporting, setExporting] = useState(false)

  // ── Visibilidad de columnas de referencia (persistida) ──────────────────────
  // Estas columnas identificadoras saturan la tabla; el usuario elige cuáles ver.
  const [visibleCols, setVisibleCols] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('pc.visibleCols')
      if (raw) return new Set(JSON.parse(raw) as string[])
    } catch { /* localStorage no disponible → todas visibles */ }
    return new Set(REF_COL_KEYS)
  })
  const showCol = (k: string) => visibleCols.has(k)
  function toggleCol(k: string) {
    setVisibleCols(prev => {
      const next = new Set(prev)
      next.has(k) ? next.delete(k) : next.add(k)
      try { localStorage.setItem('pc.visibleCols', JSON.stringify([...next])) } catch { /* ignore */ }
      return next
    })
  }
  const colMenuItems: ColumnItem[] = COLS
    .filter(c => c.refCol)
    .map(c => ({ key: c.key, label: t(c.labelKey as string) }))

  // Columnas realmente pintadas, en orden. Cabecera y pie salen de aquí, así que
  // no pueden descuadrarse entre sí.
  const tableCols = useMemo(
    () => COLS.filter(c =>
      (!c.refCol || visibleCols.has(c.key)) &&
      (!c.onlyEmpresaAll || isEmpresaAll),
    ),
    [visibleCols, isEmpresaAll],
  )

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['pedidos-compra', desde, hasta],
    queryFn:  () => pedidosCompraApi.getPedidos({ desde, hasta }),
    staleTime: 5 * 60 * 1000,
    gcTime:    10 * 60 * 1000,
  })
  const rows: PedidoCompraRow[] = useMemo(() => (data?.data ?? []) as PedidoCompraRow[], [data])

  const allEstados  = useMemo(() =>
    [...new Set(rows.map(r => r.purchstatus).filter(Boolean) as string[])].sort(), [rows])

  const filtered = useMemo(() => {
    const lqs = terminos.map(x => x.toLowerCase())
    return rows.filter(r => {
      if (empresa && r.dataareaid !== empresa) return false
      if (estado  && r.purchstatus !== estado) return false
      // Diferencias de recepción = recepción PARCIAL: pendiente ni 0 (todo recibido)
      // ni el total pedido (nada recibido) → se recibió algo pero no todo.
      if (difRecepcion && !(r.purchstatus !== 'Cancelado'
                         && r.qtyPendiente !== 0
                         && r.qtyPendiente !== r.qtyPedida)) return false
      // OR entre términos, cada uno contra todo lo que este buscador ha cubierto
      // siempre: número, proveedor, contenedor, factura del expedidor, BL, DVD y
      // artículos de las líneas.
      if (lqs.length > 0) {
        const texto = [
          r.purchid, r.vendaccount, r.nombreProveedor, r.numeroPedidoCliente,
          r.numeroContenedor, r.numeroFacturaExpedidor, r.bl, r.dvd, r.articulosBusqueda,
        ].filter(Boolean).join(' ').toLowerCase()
        if (!lqs.some(q => texto.includes(q))) return false
      }
      return true
    })
  }, [rows, empresa, estado, difRecepcion, terminos])

  // Mismo comportamiento exploratorio que Pedidos de venta: al buscar, los
  // pedidos que casan se abren solos para enseñar POR QUÉ han salido, con tope
  // porque cada fila desplegada dispara su consulta de líneas.
  const coincide = useMemo(() => predicadoArticulos(terminos, fmtArt), [terminos, fmtArt])
  const firmaBusqueda = terminos.join('|')
  useEffect(() => {
    if (!firmaBusqueda) { setExpanded(new Set()); return }
    setExpanded(filtered.length <= MAX_AUTO_DESPLIEGUE
      ? new Set(filtered.map(r => `${r.purchid}-${r.dataareaid}`))
      : new Set())
  }, [firmaBusqueda, filtered])

  function limpiarFiltros() {
    setTerminos([]); setEmpresa(''); setEstado(''); setDifRecepcion(false); setPage(1)
    // El efecto de sincronización dejará la URL sin estas claves y eso ya borra
    // el recuerdo de la sesión; `olvidar` lo hace explícito.
    olvidar()
  }

  // Los términos del buscador global van SIN prefijo: todos vienen del mismo
  // campo. Los demás sí lo llevan, porque ahí la ambigüedad es real.
  const filtrosActivos: FiltroActivo[] = [
    // El rango va como un chip más, y no sólo dentro de su control. Ahora que los
    // filtros sobreviven al cambio de pantalla, volver con un rango puesto y sin
    // rastro de él en la franja dejaba la lista corta sin explicación —y sin
    // «Limpiar todos», que sólo se pinta cuando hay algún chip.
    ...(desde || hasta
      ? [{
          clave: 'fechas', campo: t('common.filtros.fechas'),
          valor: [fechaCorta(desde), fechaCorta(hasta)].join(' – '),
          quitar: () => { setDesde(''); setHasta(''); setPage(1) },
        }]
      : []),
    ...filtrosDe(terminos, v => { setTerminos(v); setPage(1) }),
    ...(empresa ? [{
      clave: 'emp', campo: t('common.company'), valor: empresa.toUpperCase(),
      quitar: () => { setEmpresa(''); setPage(1) },
    }] : []),
    ...(estado ? [{
      clave: 'est', campo: t('common.status'),
      valor: t('purchaseOrders.status.' + estado, { defaultValue: estado }),
      quitar: () => { setEstado(''); setPage(1) },
    }] : []),
    ...(difRecepcion ? [{
      clave: 'dif', valor: t('purchaseOrders.filter.receptionDiff'),
      quitar: () => { setDifRecepcion(false); setPage(1) },
    }] : []),
  ]

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      const av = a[sortKey as keyof PedidoCompraRow]
      const bv = b[sortKey as keyof PedidoCompraRow]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return dir * (av - bv)
      return dir * String(av).localeCompare(String(bv), 'es-ES')
    })
  }, [filtered, sortKey, sortDir])

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const safePage   = Math.min(page, totalPages)
  const paged      = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  // Totales sobre todo el conjunto filtrado, no sobre la página de 50.
  const agg = useColumnAggregations({ storageKey: 'pedidosCompra', rows: sorted, columns: AGG_COLS })

  const activeFilters = (terminos.length > 0 ? 1 : 0) + (empresa ? 1 : 0) + (estado ? 1 : 0) + (difRecepcion ? 1 : 0)
  // El filtro se ensena si hay MAS DE UNA empresa que elegir, no si el rol
  // es `Empresa.All`: con AVI+TDIS habia dos empresas y ningun desplegable.
  const showEmpFilter = useVariasEmpresas()

  const kpis = useMemo(() => {
    let total = 0, pendiente = 0, importe = 0
    for (const r of filtered) {
      total++
      pendiente += r.qtyPendiente
      importe   += r.importeTotal
    }
    return { total, pendiente, importe }
  }, [filtered])

  const pageBg      = dark ? '-m-6 p-6 bg-slate-950 text-slate-100 min-h-full' : 'text-slate-900'
  const cardBg      = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200'
  const mutedTxt    = dark ? 'text-slate-400' : 'text-slate-500'
  const inputCls    = dark
    ? 'bg-slate-800 border-white/10 text-slate-100 placeholder-slate-500 focus:border-sky-500'
    : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400 focus:border-sky-400'
  const selectCls   = `${inputCls} cursor-pointer`
  const btnBase     = 'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer'
  const btnNeutral  = dark
    ? `${btnBase} border-white/10 bg-white/5 text-slate-300 hover:bg-white/10`
    : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm`
  const thBase = `px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap select-none ${dark ? 'text-slate-400' : 'text-slate-500'}`
  const tdBase = `px-3 py-2.5 text-[13px] whitespace-nowrap ${dark ? 'text-slate-300' : 'text-slate-700'}`

  function handleSort(k: PedidoCompraSortKey) {
    if (k === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(k); setSortDir('asc') }
    setPage(1)
  }

  async function exportExcel() {
    if (sorted.length === 0 || exporting) return
    setExporting(true)
    try {
      // 1. Traer en bloque las líneas de todos los pedidos filtrados
      const ids = [...new Set(sorted.map(r => r.purchid))]
      const res = await pedidosCompraApi.getLineasBulk(ids)
      const lineas = (res.data ?? []) as PedidoCompraLinea[]
      const byPedido = new Map<string, PedidoCompraLinea[]>()
      for (const l of lineas) {
        const k = `${l.numPedido}__${l.dataareaid}`
        const arr = byPedido.get(k)
        if (arr) arr.push(l); else byPedido.set(k, [l])
      }

      // 2. Construir la hoja: cabecera de pedido (nivel 0) + sus líneas (nivel 1, agrupables)
      const header = [
        ...(isEmpresaAll ? [t('purchaseOrders.excel.company')] : []),
        t('purchaseOrders.excel.orderId'),
        t('purchaseOrders.excel.customerOrderNo'),
        t('purchaseOrders.excel.containerNo'),
        t('purchaseOrders.excel.shipperInvoice'),
        t('purchaseOrders.excel.bl'),
        t('purchaseOrders.excel.dvd'),
        t('purchaseOrders.excel.supplier'),   t('purchaseOrders.excel.supplierCode'),
        t('purchaseOrders.excel.orderDate'),  t('purchaseOrders.excel.deliveryDate'),
        t('purchaseOrders.excel.status'),     t('purchaseOrders.excel.family'),
        t('purchaseOrders.excel.qtyOrdered'), t('purchaseOrders.excel.qtyReceived'),
        t('purchaseOrders.excel.qtyPending'), t('purchaseOrders.excel.amount'),
        t('purchaseOrders.excel.currency'),
        t('purchaseOrders.excel.invoice'),   t('purchaseOrders.excel.invoiceDate'),
        t('purchaseOrders.excel.billedQty'), t('purchaseOrders.excel.billedAmount'),
      ]
      const aoa: (string | number)[][] = [header]
      const rowInfo: XLSX.RowInfo[] = [{}]   // fila de títulos, sin nivel

      for (const r of sorted) {
        aoa.push([
          ...(isEmpresaAll ? [r.dataareaid] : []),
          r.purchid,
          r.numeroPedidoCliente ?? '',
          r.numeroContenedor ?? '',
          r.numeroFacturaExpedidor ?? '',
          r.bl ?? '',
          r.dvd ?? '',
          r.nombreProveedor ?? r.vendaccount, r.vendaccount,
          r.fechaPedido ?? '', r.fechaEntrega ?? '',
          r.purchstatus ?? '', r.familia ?? '',
          r.qtyPedida, r.qtyRecibida, r.qtyPendiente, r.importeTotal,
          r.moneda ?? 'EUR',
          '', '', '', '',
        ])
        rowInfo.push({ level: 0 })

        for (const l of byPedido.get(`${r.purchid}__${r.dataareaid}`) ?? []) {
          aoa.push([
            ...(isEmpresaAll ? [''] : []),
            `   └ ${l.numLinea ?? ''}`, '',
            '',
            '',
            '',
            '',
            fmtArt(l.articulo), l.descripcion ?? '',
            '', l.fechaEntrega ?? '',
            l.estadoLinea ?? '', [l.familia, l.subfamilia].filter(Boolean).join(' / '),
            l.qtyPedida, l.qtyRecibida, l.qtyPendiente, l.importe,
            l.moneda ?? 'EUR',
            l.factura ?? '', l.primeraFechaFactura ?? '',
            l.qtyFacturada ?? '', l.importeFacturado ?? '',
          ])
          rowInfo.push({ level: 1 })
        }
      }

      const ws = XLSX.utils.aoa_to_sheet(aoa)
      ws['!rows'] = rowInfo
      ws['!cols'] = header.map((_, i) => ({ wch: i <= (isEmpresaAll ? 3 : 2) ? 22 : 14 }))

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, t('purchaseOrders.excel.sheetName'))
      XLSX.writeFile(wb, `${t('purchaseOrders.excel.filename')}_${new Date().toISOString().slice(0, 10)}.xlsx`)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className={`flex flex-col gap-4 ${pageBg}`}>

      <PageHeader
        dark={dark}
        title={t('purchaseOrders.title')}
        subtitle={
          <>
            {filtered.length} {t('common.of')} {rows.length} {t('common.orders')}
            {activeFilters > 0 && (
              <span className="ml-1 text-sky-500 font-semibold">
                · {t('common.activeFilter', { count: activeFilters })}
              </span>
            )}
          </>
        }
        actions={
          <>
            {isError && <span className="text-[11px] text-red-500 font-semibold">{t('common.error')}</span>}
            <RefreshButton dark={dark} onClick={() => refetch()} isLoading={isFetching} />
          </>
        }
      />

      {/* KPIs */}
      {PAGE_KPIS.purchase && (
      <div className="flex gap-3 flex-wrap">
        <KpiCard dark={dark}
          label={t('purchaseOrders.kpi.orders')}
          value={isLoading ? '—' : String(kpis.total)}
          sub={t('purchaseOrders.kpi.ordersSub')} />
        <KpiCard dark={dark}
          label={t('purchaseOrders.kpi.pendingUnits')}
          value={isLoading ? '—' : fmtN(kpis.pendiente)}
          sub={t('purchaseOrders.kpi.pendingUnitsSub')} />
        <KpiCard dark={dark}
          label={t('purchaseOrders.kpi.totalAmount')}
          value={isLoading ? '—' : fmtEur(kpis.importe)}
          sub={t('purchaseOrders.kpi.totalAmountSub')} />
      </div>
      )}

      {/* Filters */}
      <div className={filterCardCls(dark)}>
        <div className="flex flex-col gap-3 p-3 sm:flex-row sm:flex-wrap sm:items-end">
          {/* Desde / Hasta */}
          <DateRangePicker
            dark={dark}
            desde={desde}
            hasta={hasta}
            onDesde={setDesde}
            onHasta={setHasta}
          />
          {/* Pedido / Proveedor */}
          <div className="flex flex-col gap-1 min-w-[180px] flex-1">
            <label className={filterLabelCls(dark)}>{t('purchaseOrders.filter.orderSupplier')}</label>
            {/* Multi-término: texto libre, no catálogo. Este campo cubre número,
                proveedor, contenedor, BL y artículos a la vez; un desplegable de
                artículos perdería la mitad del alcance. */}
            <MultiFiltro
              dark={dark}
              permitirLibre
              seleccion={terminos}
              onSeleccion={v => { setTerminos(v); setPage(1) }}
              placeholder={t('common.search')}
            />
          </div>
          {/* Empresa */}
          {showEmpFilter && (
            <div className="flex flex-col gap-1 min-w-[90px]">
              <label className={filterLabelCls(dark)}>{t('common.company')}</label>
              <select
                value={empresa}
                onChange={e => { setEmpresa(e.target.value); setPage(1) }}
                className={`${filterFieldCls(dark, !!empresa)} cursor-pointer`}
              >
                <option value="">{t('common.allCompanies')}</option>
                {allowedEmpresas.map(e => <option key={e} value={e}>{e.toUpperCase()}</option>)}
              </select>
            </div>
          )}
          {/* Estado */}
          <div className="flex flex-col gap-1 min-w-[140px]">
            <label className={filterLabelCls(dark)}>{t('common.status')}</label>
            <select
              value={estado}
              onChange={e => { setEstado(e.target.value); setPage(1) }}
              className={`${filterFieldCls(dark, !!estado)} cursor-pointer`}
            >
              <option value="">{t('common.allStatuses')}</option>
              {allEstados.map(s => <option key={s} value={s}>{t('purchaseOrders.status.' + s, { defaultValue: s })}</option>)}
            </select>
          </div>
          {/* Pendientes anómalos */}
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => { setDifRecepcion(v => !v); setPage(1) }}
              title={t('purchaseOrders.filter.receptionDiffTip')}
              aria-pressed={difRecepcion}
              className={`${btnBase} ${
                difRecepcion
                  ? 'border-amber-500/40 bg-amber-500/15 text-amber-500'
                  : dark
                    ? 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'
                    : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm'
              }`}
            >
              <svg className="w-3.5 h-3.5 inline mr-1 -mt-0.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 1.5L1 14h14L8 1.5zM8 6v4M8 12h.01"/>
              </svg>
              {t('purchaseOrders.filter.receptionDiff')}
            </button>
          </div>
        </div>

        {/* Franja común, separada de los controles: éstos no se mueven nunca. */}
        {filtrosActivos.length > 0 && (
          <div className="px-3 pb-3 -mt-1">
            <BarraFiltrosActivos
              dark={dark}
              filtros={filtrosActivos}
              onLimpiar={limpiarFiltros}
              resumen={`${filtered.length} ${t('common.orders')}`}
            />
          </div>
        )}
      </div>

      {/* Table */}
      <div className={`rounded-xl border overflow-hidden shadow-sm ${cardBg}`}>
        {/* Toolbar */}
        <TableToolbar
          dark={dark}
          left={
            <span className={`text-[11px] font-semibold ${mutedTxt}`}>
              {sorted.length.toLocaleString('es-ES')} {t('common.orders')}
            </span>
          }
          right={
            <>
              <label className={`text-[10px] ${mutedTxt}`}>{t('purchaseOrders.sort_label')}</label>
              <select value={sortKey} onChange={e => handleSort(e.target.value as PedidoCompraSortKey)}
                className={`text-[11px] px-2 py-1 rounded-lg border outline-none ${selectCls}`}>
                <option value="fechaEntrega">{t('purchaseOrders.sort.orderDate')}</option>
                <option value="purchid">{t('purchaseOrders.sort.orderId')}</option>
                <option value="numeroPedidoCliente">{t('purchaseOrders.sort.customerOrderNo')}</option>
                <option value="numeroContenedor">{t('purchaseOrders.sort.containerNo')}</option>
                <option value="numeroFacturaExpedidor">{t('purchaseOrders.sort.shipperInvoice')}</option>
                <option value="bl">{t('purchaseOrders.sort.bl')}</option>
                <option value="dvd">{t('purchaseOrders.sort.dvd')}</option>
                <option value="nombreProveedor">{t('purchaseOrders.sort.supplier')}</option>
                <option value="purchstatus">{t('purchaseOrders.sort.status')}</option>
                <option value="qtyPendiente">{t('purchaseOrders.sort.qtyPending')}</option>
                <option value="importeTotal">{t('purchaseOrders.sort.amount')}</option>
              </select>
              <SortDirButton dark={dark} dir={sortDir} onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')} />
              <ColumnsMenu
                dark={dark}
                items={colMenuItems}
                visible={visibleCols}
                onToggle={toggleCol}
                btnLabel={t('purchaseOrders.col.columnsBtn')}
                title={t('purchaseOrders.col.columnsTitle')}
              />
              <button
                onClick={exportExcel}
                disabled={sorted.length === 0 || exporting}
                className={`${btnNeutral} disabled:opacity-40 disabled:cursor-not-allowed`}
                title={t('common.exportExcel')}
              >
                {exporting ? (
                  <svg className="w-3.5 h-3.5 inline mr-1 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"/>
                    <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5 inline mr-1" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12h10M8 3v7M5 7l3 3 3-3"/>
                  </svg>
                )}
                {exporting ? t('common.exporting') : t('common.exportExcel')}
              </button>
            </>
          }
        />

        <BarraMarcas dark={dark} marcas={marcas} />

        {isLoading && (
          <div className="px-4 py-3 space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className={`h-10 rounded-lg animate-pulse ${dark ? 'bg-white/4' : 'bg-slate-100'}`}
                style={{ opacity: 1 - i * 0.09 }} />
            ))}
          </div>
        )}

        {!isLoading && isError && (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <svg className="w-6 h-6 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
            </svg>
            <p className={`text-[12px] font-semibold ${dark ? 'text-red-400' : 'text-red-500'}`}>{t('common.error')}</p>
            <RefreshButton dark={dark} onClick={() => refetch()} />
          </div>
        )}

        {!isLoading && !isError && paged.length === 0 && (
          <EmptyState
            dark={dark}
            title={t('purchaseOrders.noResultsTitle')}
            description={t('purchaseOrders.noResultsSub')}
            action={activeFilters > 0 ? (
              <button
                onClick={() => { setTerminos([]); setEmpresa(''); setEstado(''); setDifRecepcion(false); setPage(1) }}
                className={`text-[11px] px-3 py-1.5 rounded-lg border transition-colors cursor-pointer font-medium ${
                  dark ? 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm'
                }`}
              >
                <svg className="w-3 h-3 text-red-400 inline mr-1" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M1 1l10 10M11 1L1 11"/>
                </svg>
                {t('common.clearFilters')}
              </button>
            ) : undefined}
          />
        )}

        {!isLoading && paged.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead className={dark ? 'bg-slate-800/50' : 'bg-slate-50/80'}>
                <tr>
                  {tableCols.map(c => {
                    if (c.key === 'marca') return <CabeceraMarca key="marca" />
                    const label = c.labelKey ? t(c.labelKey) : ''
                    const menu = AGG_KEYS.has(c.key) ? (
                      <AggregationMenu
                        dark={dark}
                        colLabel={label}
                        active={agg.aggState[c.key] ?? null}
                        onChange={fn => agg.setAgg(c.key, fn)}
                      />
                    ) : null

                    if (c.sortKey) {
                      return (
                        <SortableHeader
                          key={c.key}
                          dark={dark}
                          label={label}
                          sortKey={c.sortKey}
                          activeSortKey={sortKey}
                          activeSortDir={sortDir}
                          onClick={() => handleSort(c.sortKey as PedidoCompraSortKey)}
                          align={c.align === 'right' ? 'right' : 'left'}
                          extra={menu}
                        />
                      )
                    }
                    return (
                      <th
                        key={c.key}
                        className={`group ${thBase} ${c.key === 'expand' ? 'w-8' : ''} ${c.align === 'right' ? 'text-right' : ''}`}
                      >
                        <span className={`inline-flex items-center gap-1 ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                          {label}
                          {menu}
                        </span>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {paged.map((r, idx) => {
                  const rowKey = `${r.purchid}-${r.dataareaid}`
                  const isOpen = expanded.has(rowKey)
                  // Pedido + empresa: el mismo nº de pedido puede existir en dos
                  // entidades legales, así que el número solo no identifica la fila.
                  const clave = claveFila(r.purchid, r.dataareaid)
                  return (
                    <Fragment key={`${rowKey}-${idx}`}>
                      <FilaMarcable
                        marcas={marcas}
                        clave={clave}
                        dark={dark}
                        className={`border-t transition-colors ${
                          dark ? 'border-white/4 hover:bg-white/3' : 'border-slate-100 hover:bg-slate-50/60'
                        } ${isOpen ? (dark ? 'bg-white/4' : 'bg-slate-50') : ''}`}
                      >
                        <MarcaCelda marcas={marcas} clave={clave} dark={dark} />
                        <td className={`${tdBase} pl-3 pr-1 w-8`}>
                          <button
                            onClick={() => toggleExpand(rowKey)}
                            title={isOpen ? t('purchaseOrders.btn.collapseLines') : t('purchaseOrders.btn.viewLines')}
                            className={`p-1 rounded transition-colors cursor-pointer ${
                              dark ? 'text-slate-500 hover:text-slate-200' : 'text-slate-400 hover:text-slate-700'
                            }`}
                          >
                            <svg className={`w-3.5 h-3.5 transition-transform duration-150 ${isOpen ? 'rotate-90' : ''}`}
                              viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M5 11l4-4-4-4"/>
                            </svg>
                          </button>
                        </td>
                        <td className={`${tdBase} pr-1`}>
                          <button
                            onClick={() => setAdjuntosPurchid(r.purchid)}
                            title={t('purchaseOrders.btn.viewDocs')}
                            className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg border transition-colors cursor-pointer font-medium ${
                              dark
                                ? 'border-white/10 bg-white/5 text-slate-400 hover:bg-sky-500/15 hover:text-sky-400 hover:border-sky-500/30'
                                : 'border-slate-200 bg-white text-slate-500 hover:bg-sky-50 hover:text-sky-600 hover:border-sky-200 shadow-sm'
                            }`}
                          >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M9 2H4a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V6L9 2z"/>
                              <path d="M9 2v4h4M6 9h4M6 11.5h2.5"/>
                            </svg>
                            <span>{t('purchaseOrders.btn.docs')}</span>
                          </button>
                        </td>
                        <td className={`${tdBase} font-mono font-semibold text-[11px]`}>{r.purchid}</td>
                        {showCol('customerOrderNo') && (
                          <td className={`${tdBase} text-[11px]`}>
                            {r.numeroPedidoCliente
                              ? <span className="font-mono" title={r.numeroPedidoCliente}>{r.numeroPedidoCliente}</span>
                              : <span className={mutedTxt}>—</span>}
                          </td>
                        )}
                        {showCol('containerNo') && (
                          <td className={`${tdBase} text-[11px]`}>
                            {r.numeroContenedor
                              ? <span className="font-mono" title={r.numeroContenedor}>{r.numeroContenedor}</span>
                              : <span className={mutedTxt}>—</span>}
                          </td>
                        )}
                        {showCol('shipperInvoice') && (
                          <td className={`${tdBase} text-[11px]`}>
                            {r.numeroFacturaExpedidor
                              ? <span className="font-mono" title={r.numeroFacturaExpedidor}>{r.numeroFacturaExpedidor}</span>
                              : <span className={mutedTxt}>—</span>}
                          </td>
                        )}
                        {showCol('bl') && (
                          <td className={`${tdBase} text-[11px]`}>
                            {r.bl
                              ? <span className="font-mono" title={r.bl}>{r.bl}</span>
                              : <span className={mutedTxt}>—</span>}
                          </td>
                        )}
                        {showCol('dvd') && (
                          <td className={`${tdBase} text-[11px]`}>
                            {r.dvd
                              ? <span className="font-mono" title={r.dvd}>{r.dvd}</span>
                              : <span className={mutedTxt}>—</span>}
                          </td>
                        )}
                        {isEmpresaAll && <td className={`${tdBase} text-[11px]`}>{r.dataareaid.toUpperCase()}</td>}
                        <td className={tdBase}>
                          <div className="max-w-[160px]">
                            <div className="truncate font-medium" title={r.nombreProveedor ?? r.vendaccount}>
                              {r.nombreProveedor ?? r.vendaccount}
                            </div>
                            {r.nombreProveedor && (
                              <div className={`text-[10px] truncate ${mutedTxt}`}>{r.vendaccount}</div>
                            )}
                          </div>
                        </td>
                        <td className={tdBase}>
                          <StatusBadge status={r.purchstatus} dark={dark} namespace="purchaseOrders" />
                        </td>
                        <td className={`${tdBase} text-right tabular-nums`}>{fmtDate(r.fechaEntrega)}</td>
                        <td className={`${tdBase} text-right tabular-nums font-semibold`}>
                          {fmtN(r.qtyPendiente)}
                          {r.qtyPedida > 0 && (
                            <div className={`text-[10px] font-normal ${mutedTxt}`}>
                              {t('common.of')} {fmtN(r.qtyPedida)}
                            </div>
                          )}
                        </td>
                        <td className={`${tdBase} text-right tabular-nums`}>
                          {r.importeTotal ? fmtEur(r.importeTotal) : '—'}
                          {r.moneda && r.moneda !== 'EUR' && (
                            <div className={`text-[10px] ${mutedTxt}`}>{r.moneda}</div>
                          )}
                        </td>
                      </FilaMarcable>
                      {isOpen && (
                        <LineasRow
                          purchid={r.purchid}
                          dark={dark}
                          coincide={coincide}
                          marcas={marcas}
                          clavePedido={clave}
                          onVerAdjuntosFactura={(recid, factura) => setFacturaAdj({ recid, factura })}
                        />
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
              <TableTotalsFooter
                dark={dark}
                columns={tableCols.map(c => ({
                  key: c.key,
                  decimals: AGG_COLS.find(a => a.key === c.key)?.decimals,
                  align: c.align === 'right' ? 'right' : 'left',
                }))}
                results={agg.results}
                rowCount={sorted.length}
              />
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <Pagination
            dark={dark}
            page={safePage}
            totalPages={totalPages}
            totalItems={sorted.length}
            itemLabel={t('common.orders')}
            pageLabel={t('common.page')}
            ofLabel={t('common.of')}
            onFirst={() => setPage(1)}
            onPrev={() => setPage(safePage - 1)}
            onNext={() => setPage(safePage + 1)}
            onLast={() => setPage(totalPages)}
          />
        )}
      </div>

      {adjuntosPurchid && (
        <AdjuntosModal
          dark={dark}
          pedidoId={adjuntosPurchid}
          queryKeyPrefix="compra-adjuntos"
          fetchAdjuntos={pedidosCompraApi.getAdjuntos}
          onClose={() => setAdjuntosPurchid(null)}
        />
      )}

      {/* Modal adjuntos — factura (reusa el visor, sin descarga por flujo) */}
      {facturaAdj && (
        <AdjuntosModal
          dark={dark}
          pedidoId={facturaAdj.factura}
          queryKeyPrefix={`compra-factura-adjuntos-${facturaAdj.recid}`}
          fetchAdjuntos={() => pedidosCompraApi.getAdjuntosFactura(facturaAdj.recid)}
          onClose={() => setFacturaAdj(null)}
        />
      )}
    </div>
  )
}
